import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiPage } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient.js";
import { getErrorMessage } from "#/utils/APIUtils";

const basePath = "/api/zival/zivalaggregate";

export const getAnimalById = createAsyncThunk(
  "animal/getAnimalById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${basePath}/${id}`);
      return response.data || null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);
export const findAnimalsByKey = createAsyncThunk(
  "animal/findAnimalsByKey",
  async (
    { key, page = null, filter = null, sort = null },
    { rejectWithValue }
  ) => {
    try {
      const response = await ApiClient.get(
        `${basePath}/page`,
        null,
        page instanceof ApiPage ? page.toQueryString() : page,
        filter,
        sort,
        false
      );
      return { key, data: response.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const findAnimalByKmgMidAndAnimalIdentifier = createAsyncThunk(
  "animal/findAnimalByKmgMidAndAnimalIdentifier",
  async ({ key = "byDefaultKey", zivalIdentifikator }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${basePath}/search?zivalIdentifikator=${zivalIdentifikator}`
      );
      return { key, data: response.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const findAnimals = createAsyncThunk(
  "animal/findAnimals",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${basePath}/page`,
        undefined,
        page,
        filter,
        sort,
        false, // stripQueryParamsForCancelPrevious -> cancel previous list request
        true
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState = {
  isLoading: false,
  error: null,
  page: {},
  details: {},
  list: [],
  byId: {},

  entities: {},

  searchByKey: {},
  pageByKey: {},
  listByKey: {},
  isLoadingByKey: {},
  isLoadingById: {},
  errorByKey: {},
};
// --- Slice ----------------------------------------------------------------

const animalSlice = createSlice({
  name: "animal",
  initialState,
  reducers: {
    clearCurrentAnimal(state) {
      state.details = null;
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    const upsertEntities = (state, content = []) => {
      for (const a of content) if (a?.id) state.entities[a.id] = a;
    };
    builder
      .addCase(findAnimals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(findAnimals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const data = action.payload;
        state.page = data || null;
        state.list = data?.content || [];
        action.payload?.content?.forEach((animal) => {
          state.byId[animal.id] = animal;
        });
      })
      .addCase(findAnimals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action?.payload || action?.error?.message || null;
      });
    builder
      .addCase(findAnimalsByKey.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.isLoadingByKey[key] = true;
        state.errorByKey[key] = null;
      })
      .addCase(findAnimalsByKey.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.isLoadingByKey[key] = false;
        state.pageByKey[key] = data || {};
        state.listByKey[key] = data?.content || [];
        upsertEntities(state, data?.content || []);
      })
      .addCase(findAnimalsByKey.rejected, (state, action) => {
        const { key } = action.meta.arg;
        state.isLoadingByKey[key] = false;
        state.errorByKey[key] = action?.payload || action?.error?.message || {};
      });
    builder
      .addCase(
        findAnimalByKmgMidAndAnimalIdentifier.pending,
        (state, action) => {
          const key = action.meta.arg.key;
          state.isLoadingByKey[key] = true;
          state.errorByKey[key] = null;
        }
      )
      .addCase(
        findAnimalByKmgMidAndAnimalIdentifier.fulfilled,
        (state, action) => {
          const { key, data } = action.payload;
          state.isLoadingByKey[key] = false;
          state.searchByKey[key] = data || [];
          state.listByKey[key] = data || [];
          upsertEntities(state, data || []);
        }
      )
      .addCase(
        findAnimalByKmgMidAndAnimalIdentifier.rejected,
        (state, action) => {
          const { key } = action.meta.arg;
          state.isLoadingByKey[key] = false;
          state.errorByKey[key] =
            action?.payload || action?.error?.message || {};
        }
      );
    builder
      .addCase(getAnimalById.pending, (state, action) => {
        const id = action.meta.arg;

        state.isLoading = true;
        state.isLoadingById[id] = true;
        state.error = null;
      })
      .addCase(getAnimalById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        const item = action.payload;
        if (!item) return;
        state.details = item;
        const { id } = item;
        if (id) {
          state.byId[id] = item;
          state.isLoadingById[id] = false;
          state.entities[id] = item;
        }
      })
      .addCase(getAnimalById.rejected, (state, action) => {
        const id = action.meta.arg;

        state.isLoading = false;
        state.isLoadingById[id] = false;

        state.error = action.payload || null;
        state.details = null;
      });
  },
});
/**
 * Example response item shape (for reference) - SUBJECT TO CHANGE at this STAGE
 *
 *  {
 *       "createdOn": "2025-12-03T14:50:43.591106Z",
 *       "updatedOn": null,
 *       "version": 1,
 *       "id": "4d5286ab-b548-4f0f-a095-743fb7ee665f",
 *       "speciesSifra": "KOZA",
 *       "matiId": null,
 *       "matiUsesnaStevilka": null,
 *       "matiRodovniskaStevilka": null,
 *       "matiPotrditev": null,
 *       "oceId": null,
 *       "oceUsesnaStevilka": null,
 *       "oceRodovniskaStevilka": null,
 *       "ocePotrditev": null,
 *       "koefInbriding": null,
 *       "razvojniStadijSifra": "ODRASEL",
 *       "spolSifra": "MOSKI",
 *       "usesnaStevilka": "1000007",
 *       "rodovniskaStevilka": null,
 *       "rodovniskaKnjigaId": null,
 *       "rodovniskaKnjigaNaziv": null,
 *       "razdelekSifra": null,
 *       "razredRodovniskeKnjigeSifra": null,
 *       "priznanaPasmaId": "db0ef00e-f532-480f-a40a-9f51880f507e",
 *       "priznanaPasmaSifra": "222",
 *       "priznanaPasmaNazivKey": "PASMA_222_NAZIV",
 *       "priznanaPasmaSl": "Koza 222",
 *       "priznanaPasmaEn": "Goat 222",
 *       "rojstvoDatum": null,
 *       "smrtDatum": null,
 *       "statusSifra": "AKTIVNA",
 *       "rejecId": "6d9e86a2-9117-4c03-a59d-ffc9ff293e8d",
 *       "rejecSifra": "GRD-135/53",
 *       "rejecKmgMid": 1234567890,
 *       "rejecNaziv": "Kmetijstvo Grdadolnik",
 *       "lastnikId": "6d9e86a2-9117-4c03-a59d-ffc9ff293e8d",
 *       "lastnikSifra": "GRD-135/53",
 *       "lastnikKmgMid": 1234567890,
 *       "lastnikNaziv": "Kmetijstvo Grdadolnik",
 *       "trenutnaLokacijaId": null,
 *       "trenutnaLokacijaDatumPrihoda": null,
 *       "trenutnaLokacijaVzrocniZapisId": null,
 *       "trenutnaLokacijaVzrocniZapisSifra": null,
 *       "trenutnaLokacijaGmidId": null,
 *       "trenutnaLokacijaGmid": null,
 *       "trenutnaLokacijaGmidNaziv": null,
 *       "trenutnaLokacijaKmgId": null,
 *       "trenutnaLokacijaKmgMid": null,
 *       "trenutnaLokacijaKmgNaziv": null,
 *       "zivalDatumKreiranja": "2025-12-11T01:43:52.968039Z",
 *       "zivalDatumZadnjeSpremembe": "2025-05-17T00:00:00Z",
 *       "zivalPodatkiZadnjaSpremembaSistemRecordId": null,
 *       "zivalPodatkiZadnjaSpremembaSistemOpomba": null,
 *       "zivalPodatkiZadnjaSpremembaSistemTipSpremembe": null,
 *       "updatedByName": "/",
 *       "createdByName": "IS POKK"
 *     }
 **/
export const { clearCurrentAnimal } = animalSlice.actions;

export default animalSlice.reducer;
